import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { blogPosts, categories } from '../data/blogPosts';

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
}

// ─── Decorative leaf SVG ─────────────────────────────────────────────────────

function LeafIcon({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17 8C8 10 5.9 16.17 3.82 19.54A1 1 0 005.2 20.9C6.24 19.29 7.71 18 9 18c1.5 0 3 1 5 1 5 0 6-4 6-6V8c-.6.2-2 0-3 0z"/>
    </svg>
  );
}

// ─── Category pill ───────────────────────────────────────────────────────────

function CategoryPill({ label, active, dark, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`text-xs font-semibold px-4 py-2 rounded-full border transition-all duration-200
        ${active
          ? 'bg-green-700 border-green-700 text-white'
          : dark
            ? 'bg-transparent border-green-400/15 text-green-400 hover:border-green-400/40 hover:bg-green-400/5'
            : 'bg-white border-green-800/15 text-green-700 hover:border-green-600 hover:bg-green-50'
        }`}
    >
      {label}
    </button>
  );
}

// ─── Post card ───────────────────────────────────────────────────────────────

function PostCard({ post, dark }) {
  const navigate = useNavigate();
  const colors = {
    'Greenhouse Technology': { bg: 'bg-green-100', text: 'text-green-800', darkBg: 'bg-green-400/10', darkText: 'text-green-400' },
    'Sustainability':        { bg: 'bg-emerald-100', text: 'text-emerald-800', darkBg: 'bg-emerald-400/10', darkText: 'text-emerald-400' },
    'Quality & Standards':  { bg: 'bg-lime-100', text: 'text-lime-800', darkBg: 'bg-lime-400/10', darkText: 'text-lime-400' },
    'Innovation':            { bg: 'bg-teal-100', text: 'text-teal-800', darkBg: 'bg-teal-400/10', darkText: 'text-teal-400' },
    'Food Security':         { bg: 'bg-green-100', text: 'text-green-800', darkBg: 'bg-green-400/10', darkText: 'text-green-400' },
  };
  const cat = colors[post.category] || colors['Greenhouse Technology'];

  return (
    <article
      onClick={() => { navigate(`/blog/${post.slug}`); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
      className={`group cursor-pointer border rounded-2xl overflow-hidden transition-all duration-300 flex flex-col
        ${dark
          ? 'bg-green-900/20 border-green-400/10 hover:border-green-600/40 hover:shadow-xl hover:shadow-green-400/5'
          : 'bg-white border-green-800/10 hover:border-green-600 hover:shadow-xl hover:-translate-y-1'}`}
    >
      {/* Illustration header */}
      <div className={`h-44 flex items-center justify-center relative overflow-hidden
        ${dark ? 'bg-green-900/40' : 'bg-green-50'}`}>
        <div className="absolute inset-0 opacity-[0.05]"
          style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)', backgroundSize: '20px 20px' }}
        />
        <PostIllustration category={post.category} dark={dark} />
        <span className={`absolute top-3 left-4 text-xs font-semibold px-3 py-1 rounded-full
          ${dark ? `${cat.darkBg} ${cat.darkText}` : `${cat.bg} ${cat.text}`}`}>
          {post.category}
        </span>
      </div>

      {/* Content */}
      <div className="p-6 flex flex-col flex-1">
        <div className={`flex items-center gap-3 text-xs mb-3 ${dark ? 'text-green-500' : 'text-green-500'}`}>
          <span>{formatDate(post.date)}</span>
          <span>·</span>
          <span>{post.readTime}</span>
        </div>
        <h3 className={`font-serif text-xl font-bold leading-snug mb-3 group-hover:text-green-600 transition-colors
          ${dark ? 'text-white' : 'text-green-900'}`}>
          {post.title}
        </h3>
        <p className={`text-sm leading-relaxed flex-1 mb-5 ${dark ? 'text-green-300/60' : 'text-green-700/70'}`}>
          {post.excerpt}
        </p>
        <div className="flex items-center gap-2">
          <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0
            ${dark ? 'bg-green-700' : 'bg-green-100'}`}>
            <LeafIcon className={`w-3.5 h-3.5 ${dark ? 'text-green-300' : 'text-green-700'}`} />
          </div>
          <span className={`text-xs font-medium ${dark ? 'text-green-400' : 'text-green-600'}`}>{post.author}</span>
          <span className={`ml-auto text-xs font-semibold flex items-center gap-1 group-hover:gap-2 transition-all
            ${dark ? 'text-green-400' : 'text-green-600'}`}>
            Read more <span>→</span>
          </span>
        </div>
      </div>
    </article>
  );
}

// ─── Simple per-category SVG illustrations ───────────────────────────────────

export function PostIllustration({ category, dark }) {
  const stroke = dark ? 'rgba(74,222,128,0.35)' : 'rgba(22,101,52,0.25)';
  const fill   = dark ? 'rgba(74,222,128,0.08)' : 'rgba(22,101,52,0.06)';

  const illustrations = {
    'Greenhouse Technology': (
      <svg viewBox="0 0 200 120" className="w-36 h-auto opacity-80">
        <polygon points="100,10 20,60 180,60" fill={fill} stroke={stroke} strokeWidth="1.5"/>
        <rect x="20" y="60" width="160" height="50" fill={fill} stroke={stroke} strokeWidth="1.5"/>
        <line x1="60" y1="35" x2="60" y2="60" stroke={stroke} strokeWidth="1"/>
        <line x1="100" y1="10" x2="100" y2="60" stroke={stroke} strokeWidth="1"/>
        <line x1="140" y1="35" x2="140" y2="60" stroke={stroke} strokeWidth="1"/>
        <rect x="88" y="80" width="24" height="30" rx="2" fill={fill} stroke={stroke} strokeWidth="1"/>
        <ellipse cx="55" cy="95" rx="12" ry="7" fill={stroke}/>
        <ellipse cx="145" cy="95" rx="12" ry="7" fill={stroke}/>
      </svg>
    ),
    'Sustainability': (
      <svg viewBox="0 0 200 120" className="w-36 h-auto opacity-80">
        <circle cx="100" cy="60" r="40" fill={fill} stroke={stroke} strokeWidth="1.5"/>
        <path d="M100,20 Q130,40 120,70 Q100,90 80,70 Q70,40 100,20Z" fill={stroke}/>
        <path d="M100,60 Q115,50 125,60" fill="none" stroke={dark ? 'rgba(74,222,128,0.6)' : 'rgba(22,101,52,0.5)'} strokeWidth="1.5"/>
        <circle cx="60" cy="35" r="8" fill={fill} stroke={stroke} strokeWidth="1"/>
        <circle cx="140" cy="35" r="6" fill={fill} stroke={stroke} strokeWidth="1"/>
        <circle cx="50" cy="85" r="5" fill={fill} stroke={stroke} strokeWidth="1"/>
        <circle cx="150" cy="85" r="7" fill={fill} stroke={stroke} strokeWidth="1"/>
      </svg>
    ),
    'Quality & Standards': (
      <svg viewBox="0 0 200 120" className="w-36 h-auto opacity-80">
        <path d="M100,10 L120,50 L165,57 L135,86 L142,130 L100,108 L58,130 L65,86 L35,57 L80,50 Z" fill={fill} stroke={stroke} strokeWidth="1.5"/>
        <path d="M80,60 L92,72 L118,50" fill="none" stroke={dark ? 'rgba(74,222,128,0.7)' : 'rgba(22,101,52,0.6)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    'Innovation': (
      <svg viewBox="0 0 200 120" className="w-36 h-auto opacity-80">
        <circle cx="100" cy="55" r="28" fill={fill} stroke={stroke} strokeWidth="1.5"/>
        <line x1="100" y1="15" x2="100" y2="27" stroke={stroke} strokeWidth="1.5" strokeLinecap="round"/>
        <line x1="100" y1="83" x2="100" y2="95" stroke={stroke} strokeWidth="1.5" strokeLinecap="round"/>
        <line x1="140" y1="55" x2="152" y2="55" stroke={stroke} strokeWidth="1.5" strokeLinecap="round"/>
        <line x1="48" y1="55" x2="60" y2="55" stroke={stroke} strokeWidth="1.5" strokeLinecap="round"/>
        <circle cx="100" cy="55" r="10" fill={stroke}/>
        <rect x="88" y="92" width="24" height="6" rx="3" fill={fill} stroke={stroke} strokeWidth="1"/>
        <rect x="92" y="98" width="16" height="5" rx="2" fill={fill} stroke={stroke} strokeWidth="1"/>
      </svg>
    ),
    'Food Security': (
      <svg viewBox="0 0 200 120" className="w-36 h-auto opacity-80">
        <ellipse cx="100" cy="80" rx="60" ry="20" fill={fill} stroke={stroke} strokeWidth="1"/>
        <path d="M60,80 Q70,40 100,20 Q130,40 140,80" fill={fill} stroke={stroke} strokeWidth="1.5"/>
        <path d="M75,80 Q80,55 100,40 Q120,55 125,80" fill={stroke} opacity=".5"/>
        <line x1="100" y1="20" x2="100" y2="80" stroke={dark ? 'rgba(74,222,128,0.6)' : 'rgba(22,101,52,0.5)'} strokeWidth="1.5"/>
      </svg>
    ),
  };

  return illustrations[category] || illustrations['Greenhouse Technology'];
}

// ─── Main BlogPage ────────────────────────────────────────────────────────────

export default function BlogPage({ dark }) {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  const filtered = blogPosts.filter(p => {
    const matchCat = activeCategory === 'All' || p.category === activeCategory;
    const q = searchQuery.toLowerCase();
    const matchSearch = !q || p.title.toLowerCase().includes(q) || p.excerpt.toLowerCase().includes(q) || p.category.toLowerCase().includes(q);
    return matchCat && matchSearch;
  });

  const goToPost = (post) => {
    navigate(`/blog/${post.slug}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className={`min-h-screen pt-[70px] ${dark ? 'bg-[#0d1a0f]' : 'bg-white'}`}>
      {/* Hero banner */}
      <div className={`py-20 px-6 border-b relative overflow-hidden ${dark ? 'bg-green-900/20 border-green-400/10' : 'bg-green-50 border-green-800/8'}`}>
        <div className="absolute inset-0 opacity-[0.04]"
          style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)', backgroundSize: '28px 28px' }}
        />
        <div className="max-w-6xl mx-auto relative z-10">
          <span className={`inline-block text-xs font-semibold tracking-widest uppercase px-4 py-1.5 rounded-full mb-5
            ${dark ? 'bg-green-400/10 text-green-400' : 'bg-green-100 text-green-800'}`}>
            GEA Farm Blog
          </span>
          <h1 className={`font-serif text-4xl md:text-5xl font-bold leading-tight mb-4 max-w-2xl ${dark ? 'text-white' : 'text-green-900'}`}>
            Insights on Sustainable Agriculture
          </h1>
          <p className={`text-base leading-relaxed max-w-xl ${dark ? 'text-green-300/70' : 'text-green-700/80'}`}>
            Explore articles on greenhouse technology, sustainability, food safety, and the future of farming from the GEA Farm team.
          </p>
        </div>
      </div>

      {/* Filters + Search */}
      <div className={`sticky top-[70px] z-30 border-b backdrop-blur-md px-6 py-4 ${dark ? 'bg-[#0d1a0f]/90 border-green-400/10' : 'bg-white/90 border-green-800/8'}`}>
        <div className="max-w-6xl mx-auto flex flex-wrap items-center gap-3">
          <div className="flex flex-wrap gap-2 flex-1">
            {categories.map(c => (
              <CategoryPill
                key={c}
                label={c}
                active={activeCategory === c}
                dark={dark}
                onClick={() => setActiveCategory(c)}
              />
            ))}
          </div>
          <div className="relative">
            <svg className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${dark ? 'text-green-500' : 'text-green-400'}`}
              fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
            <input
              type="text"
              placeholder="Search articles…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className={`pl-9 pr-4 py-2 text-sm rounded-xl border outline-none transition-all w-48 focus:w-60
                ${dark
                  ? 'bg-green-900/30 border-green-400/15 text-green-200 placeholder-green-600 focus:border-green-400/40'
                  : 'bg-green-50 border-green-800/12 text-green-800 placeholder-green-400 focus:border-green-600'}`}
            />
          </div>
        </div>
      </div>

      {/* Post grid */}
      <div className="max-w-6xl mx-auto px-6 py-16">
        {filtered.length === 0 ? (
          <div className="text-center py-24">
            <LeafIcon className={`w-10 h-10 mx-auto mb-4 ${dark ? 'text-green-700' : 'text-green-200'}`} />
            <p className={`text-lg font-serif font-bold mb-2 ${dark ? 'text-white' : 'text-green-900'}`}>No articles found</p>
            <p className={`text-sm ${dark ? 'text-green-500' : 'text-green-500'}`}>Try a different search term or category.</p>
            <button
              onClick={() => { setSearchQuery(''); setActiveCategory('All'); }}
              className="mt-6 text-sm font-medium text-green-600 hover:text-green-700 underline"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <>
            <p className={`text-sm mb-8 ${dark ? 'text-green-500' : 'text-green-500'}`}>
              {filtered.length} article{filtered.length !== 1 ? 's' : ''}
              {activeCategory !== 'All' ? ` in ${activeCategory}` : ''}
              {searchQuery ? ` matching "${searchQuery}"` : ''}
            </p>

            {/* Featured post */}
            {activeCategory === 'All' && !searchQuery && (
              <article
                onClick={() => goToPost(filtered[0])}
                className={`group cursor-pointer border rounded-2xl overflow-hidden mb-8 transition-all duration-300
                  ${dark
                    ? 'bg-green-900/20 border-green-400/10 hover:border-green-600/40 hover:shadow-xl'
                    : 'bg-white border-green-800/10 hover:border-green-600 hover:shadow-xl hover:-translate-y-0.5'}`}
              >
                <div className="grid grid-cols-1 md:grid-cols-2">
                  <div className={`h-64 md:h-auto flex items-center justify-center relative overflow-hidden ${dark ? 'bg-green-900/40' : 'bg-green-50'}`}>
                    <div className="absolute inset-0 opacity-[0.05]"
                      style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)', backgroundSize: '20px 20px' }}
                    />
                    <div className="scale-150">
                      <PostIllustration category={filtered[0].category} dark={dark} />
                    </div>
                    <span className={`absolute top-4 left-4 text-xs font-bold px-3 py-1.5 rounded-full
                      ${dark ? 'bg-green-400/10 text-green-400' : 'bg-green-100 text-green-800'}`}>
                      Featured
                    </span>
                  </div>
                  <div className="p-8 flex flex-col justify-center">
                    <span className={`text-xs font-semibold mb-3 ${dark ? 'text-green-400' : 'text-green-600'}`}>
                      {filtered[0].category}
                    </span>
                    <h2 className={`font-serif text-2xl font-bold leading-snug mb-4 group-hover:text-green-600 transition-colors
                      ${dark ? 'text-white' : 'text-green-900'}`}>
                      {filtered[0].title}
                    </h2>
                    <p className={`text-sm leading-relaxed mb-6 ${dark ? 'text-green-300/60' : 'text-green-700/70'}`}>
                      {filtered[0].excerpt}
                    </p>
                    <div className={`flex items-center gap-3 text-xs ${dark ? 'text-green-500' : 'text-green-500'}`}>
                      <span>{formatDate(filtered[0].date)}</span>
                      <span>·</span>
                      <span>{filtered[0].readTime}</span>
                      <span className={`ml-auto font-semibold flex items-center gap-1 group-hover:gap-2 transition-all
                        ${dark ? 'text-green-400' : 'text-green-600'}`}>
                        Read article →
                      </span>
                    </div>
                  </div>
                </div>
              </article>
            )}

            {/* Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {(activeCategory === 'All' && !searchQuery ? filtered.slice(1) : filtered).map(post => (
                <PostCard key={post.id} post={post} dark={dark} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
